import React, { useRef } from 'react';
import { View, Animated, ScrollView, Text, StyleSheet } from 'react-native';
import i18n from 'i18n-js';

import Item from './item';
import Header from './header/List';
import StatusBlock from './header/components/StatusBlock';
import TouchableElement from '@components/common/touchableElement';
import FeatureFlags from '@components/featureFlags';
import useStatusBar from '@components/common/useStatusBar';
import { SmallLoading } from '@components/loading';

import { isPhone, machineStatus, ALL_MACHINES, isWeb, chartStatuses } from '@helpers';
import { scale, notchTop } from '@components/frame';

import ColorWrapper from '@styles/colors';
import { font } from '@styles/fonts';

const List = ({
  listWidth,
  componentId,
  data,
  selectedItem,
  setSelectedMachine,
  timeOffset,
  C,
  updateAppValue,
  machines = {},
  generateImageURL,
  initialLoading,
  machinesLoaded,
  selectedTime,
  setSelectedTime,
  selectedPeriod,
  setSelectedPeriod,
  machineStatistics,
  machineListOEE,
  selectedGroup,
  setSelectedGroup,
}) => {
  const count = {
    [machineStatus.ACTIVE]: 0,
    [machineStatus.SETUP]: 0,
    [machineStatus.CHANGE]: 0,
    [machineStatus.NOTPRODUCTIVE]: 0,
  };

  useStatusBar({
    C,
  });

  data.forEach((item) => (count[item.machineStatus.status] += 1));

  const status = [
    {
      color: C('Status / Light / Active'),
      count: count[machineStatus.ACTIVE],
      name: i18n.t('Active'),
    },
    {
      color: C('Status / Light / Change'),
      count: count[machineStatus.CHANGE],
      name: i18n.t('Change'),
    },
    {
      color: C('Status / Light / Setup'),
      count: count[machineStatus.SETUP],
      name: i18n.t('Setup'),
    },
    {
      color: C('Status / Light / Not Productive'),
      count: count[machineStatus.NOTPRODUCTIVE],
      name: i18n.t('Not Productive'),
    },
  ];

  const fadeAnim = useRef(new Animated.Value(0)).current;
  const marginTop = isPhone ? scale(76) + notchTop : 50;
  const animationDuration = scale(100);
  const displayTime = selectedTime === 'now';
  const isUtilization = selectedPeriod === chartStatuses.utilization;
  const isTimeline = selectedPeriod === chartStatuses.timeline;
  const allSelected = selectedItem?.id === ALL_MACHINES;

  const selectedGroupIDs = selectedGroup?.machines.map((i) => Number(i.machineId)) || [];

  const renderArray = (() => {
    if (displayTime || isTimeline) {
      return data;
    }

    if (isUtilization) {
      return machineListOEE?.data;
    }

    return machineStatistics.data;
  })().filter((i) => (selectedGroup ? selectedGroupIDs.includes(Number(i.id)) : true));

  const additionalData = (() => {
    if (displayTime || isTimeline) {
      return {};
    }

    if (isUtilization) {
      return {};
    }

    return {
      maxValueActive: machineStatistics.maxValueActive,
      maxValueProductive: machineStatistics.maxValueProductive,
    };
  })();

  const showLoading = (() => {
    if (displayTime || isTimeline) {
      return false;
    }

    if (isUtilization) {
      return !machineListOEE?.loaded;
    }

    return !machineStatistics.loaded;
  })();

  return [
    <FeatureFlags {...{ name: 'general-showapiresponsetime', C }} key="request-time">
      <View style={{ position: 'absolute', zIndex: 1000000, top: scale(35), left: scale(20) }}>
        <Text style={{ color: C('Label/Light/Quinary'), fontSize: 12, marginBottom: 10 }}>
          Request - ({machines.requestTimeEndMS} ms) |{' '}
          {machines && machines.refreshInfo && machines.refreshInfo.start
            ? `Refresh token - ${machines.refreshInfo.start} - ${machines.refreshInfo.start}`
            : 'Token is valid'}
        </Text>
      </View>
    </FeatureFlags>,
    <View
      style={[
        {
          width: isPhone ? '100%' : listWidth,
          backgroundColor: C('Background/Light/System'),
          borderRightColor: C('Hairline / Light / No trans'),
          borderRightWidth: isPhone ? 0 : isWeb ? 1 : StyleSheet.hairlineWidth,
        },
      ]}
      key="machines-scroll"
    >
      <Animated.View
        style={{
          transform: [
            {
              translateY: fadeAnim.interpolate({
                inputRange: [-1, 0, animationDuration, animationDuration + 1],
                outputRange: [0, 0, -animationDuration, -animationDuration],
              }),
            },
          ],
          width: '100%',
          position: 'absolute',
          backgroundColor: C('Background / Light / System'),
          zIndex: 1000,
        }}
      >
        <Header
          {...{
            text: i18n.t('Machines'),
            fadeAnim,
            marginTop,
            animationDuration,
            selectedTime,
            setSelectedTime,
            selectedGroup,
            setSelectedGroup,
          }}
          key="header"
        />
        {isPhone && <StatusBlock {...{ status }} />}
      </Animated.View>
      <ScrollView
        scrollEventThrottle={8}
        showsVerticalScrollIndicator={false}
        // contentContainerStyle={{ paddingBottom: scale(100) }}
        onScroll={Animated.event([{ nativeEvent: { contentOffset: { y: fadeAnim } } }], {
          useNativeDriver: false,
        })}
      >
        <Header
          {...{
            text: i18n.t('Machines'),
            dummy: true,
            marginTop,
            selectedTime,
            setSelectedTime,
          }}
          key="header"
        />
        {isPhone && <StatusBlock {...{ status, dummy: true }} />}
        {!isPhone &&
          (selectedTime === 'now' || isTimeline ? (
            <StatusBlock {...{ status }} />
          ) : (
            <View style={{ width: '100%', height: scale(41) }}>
              <TouchableElement
                onPress={() => {
                  if (selectedTime === 'now') {
                    setSelectedTime('day');
                  }
                  if (selectedTime !== 'day') {
                    if (selectedPeriod == chartStatuses.events) {
                      setSelectedPeriod(chartStatuses.productive);
                    }
                  }
                  setSelectedMachine({
                    id: ALL_MACHINES,
                    fullName: i18n.t('All Machines'),
                    shortName: i18n.t('All Machines'),
                    allSelected: true,
                    machineStatus: {
                      status: machineStatus.ACTIVE,
                    },
                  });
                }}
              >
                {({ boxStyle, pressed, hovered, pressHoverColor }) => (
                  <View style={{ paddingHorizontal: scale(pressed || hovered || allSelected ? 16 : 26) }}>
                    <View
                      style={[
                        boxStyle,
                        {
                          borderColor: allSelected ? 'transparent' : C('Hairline/Light/Primary'),
                          borderTopWidth: StyleSheet.hairlineWidth,
                          borderBottomWidth: StyleSheet.hairlineWidth,
                          width: '100%',
                          height: scale(41),
                          justifyContent: 'center',
                          alignItems: 'center',
                          backgroundColor: C(
                            pressHoverColor(allSelected ? 'Selected/Light/List item' : isWeb ? 'Background/Light/System' : 'Background/Light/Field')
                          ),
                          borderRadius: scale(pressed || hovered || allSelected ? 9 : 0),
                        },
                      ]}
                    >
                      <Text style={[font['Headline'], { color: C(allSelected ? 'Label/Light/AlwaysLight Primary' : 'Label/Light/Primary') }]}>
                        {i18n.t('All Machines')}
                      </Text>
                    </View>
                  </View>
                )}
              </TouchableElement>
            </View>
          ))}
        {showLoading ? (
          <View style={{ width: '100%', marginTop: scale(150) }}>
            <SmallLoading />
          </View>
        ) : (
          renderArray?.map((item, index) => (
            <View style={{ position: 'relative' }} key={`listitem-${item.machineId}`}>
              <Item
                {...{
                  generateImageURL,
                  item: {
                    machineStatus: {},
                    ...item,
                    ...additionalData,
                  },
                  blobAccessInfo: machines.blobAccessInfo,
                  componentId,
                  setSelectedMachine,
                  timeOffset,
                  updateAppValue,
                  nextSelected: renderArray[index + 1] ? renderArray[index + 1].id === selectedItem?.id : false,
                  selected: selectedItem?.id === item?.id,
                  loading: !machinesLoaded || initialLoading,
                  displayTime,
                  selectedPeriod,
                  listWidth,
                }}
              />
            </View>
          ))
        )}
      </ScrollView>
    </View>,
  ];
};

export default ColorWrapper(List);
